import { Chip, Tooltip } from '@mui/material'
import { WorkspacePremium } from '@mui/icons-material'
import { Bundle } from './bundleCopy'

/**
 * Which bundle a page belongs to, beside its title.
 *
 * Reported from production use: the Custom Enterprise Frameworks page
 * "shows what it is about but lacks bundle name". The page explained its own
 * process and never said which bundle it came with, so:
 *
 *   - a tenant WITHOUT the bundle learned it only from the teaser lower down,
 *     after reading a description of something they cannot use
 *   - a tenant WITH the bundle was never told what they were getting it from,
 *     which matters at renewal more than it does on any given Tuesday
 *
 * So the badge is shown in BOTH cases. It is not a lock and not an upsell —
 * `BundleLocked` and `BundleTeaser` do those jobs where a capability is
 * actually refused. This is provenance: this surface is part of that bundle.
 *
 * Why not only when refused
 * -------------------------
 * A badge that appeared only for tenants who lack the bundle would be an
 * advert wearing a label, and it would teach the opposite lesson — that a
 * bundle name on screen means "you do not have this". Shown always, it means
 * what it says.
 */
interface BundleBadgeProps {
  bundle: Bundle
  /** True where the tenant does not hold it. Changes the tooltip, never
   *  whether the badge appears. */
  refused?: boolean
}

const BundleBadge = ({ bundle, refused }: BundleBadgeProps) => (
  <Tooltip
    title={
      refused
        ? `This capability is part of ${bundle}, which is not on your account.`
        : `Part of ${bundle}, included on your account.`
    }
  >
    <Chip
      size="small"
      variant="outlined"
      color={refused ? 'default' : 'success'}
      icon={<WorkspacePremium sx={{ fontSize: 15 }} />}
      label={bundle}
      sx={{ ml: 1, verticalAlign: 'middle', fontWeight: 600 }}
    />
  </Tooltip>
)

export default BundleBadge
